import { jsPDF } from 'jspdf';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import moment from 'moment';
import { getPayloadAgentName } from './agentUtil';

const PAGE_W = 210;
const PAGE_H = 297;
const ML = 15; // margin left
const MR = 15; // margin right
const MT = 15; // margin top
const MB = 20; // margin bottom (footer space)
const CONTENT_W = PAGE_W - ML - MR;

const IMAGE_MEDIA_TYPES = new Set([
  'jpeg',
  'jpg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tiff',
  'heic',
  'heif'
]);

const isImageMedia = item => {
  if (!item || !item.Media) return false;
  const t = String(item.Media.mediaType || '').toLowerCase();
  return t.startsWith('image/') || IMAGE_MEDIA_TYPES.has(t);
};

const isPDFMedia = item => {
  if (!item || !item.Media) return false;
  return String(item.Media.mediaType || '').toLowerCase() === 'pdf';
};

const capitalize = s => (s ? s[0].toUpperCase() + s.slice(1) : '');

// ── Mapbox Static Images ──────────────────────────────────
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiamFua2VlcyIsImEiOiJjand3MG1iYnAwZHBiNDhyenpwejVkbWxjIn0.UAB-FFpQn2QiHOMEDLEBNg';
const MAPBOX_OUTDOORS_STYLE = 'jankees/cleijg9zb000t01pn0thsmz0q';
const MAPBOX_SATELLITE_STYLE = 'jankees/cleijxlii005901qghunc7bis';

/**
 * Fetch a Mapbox Static Image as a JPEG data-URL.
 * Uses crossOrigin='anonymous' – Mapbox serves proper CORS headers.
 */
const fetchMapThumbnail = (styleId, lng, lat) => {
  const marker = `pin-s+e74c3c(${lng},${lat})`;
  const url = `https://api.mapbox.com/styles/v1/${styleId}/static/${marker}/${lng},${lat},13/600x375@2x?access_token=${MAPBOX_TOKEN}`;
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

/**
 * Fetch an image URL with session credentials, then rasterise it through a
 * canvas so jsPDF gets a plain JPEG data-URL regardless of the original format.
 * Using fetch() avoids the CORS-tainted-canvas problem that occurs when
 * setting crossOrigin on an <img> element.
 */
const loadImageAsDataURL = async url => {
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);
    return await new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectURL);
        try {
          const MAX = 900;
          let w = img.naturalWidth || 200;
          let h = img.naturalHeight || 200;
          if (w > MAX || h > MAX) {
            if (w >= h) {
              h = Math.round((h * MAX) / w);
              w = MAX;
            } else {
              w = Math.round((w * MAX) / h);
              h = MAX;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectURL);
        resolve(null);
      };
      img.src = objectURL;
    });
  } catch {
    return null;
  }
};

/** Fetch a PDF URL and return its bytes as an ArrayBuffer, or null on failure. */
const fetchPDFBytes = async url => {
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) return null;
    return await response.arrayBuffer();
  } catch {
    return null;
  }
};

/** Returns updated y, adding a new page if needed. */
const checkPage = (doc, y, needed = 10) => {
  if (y + needed > PAGE_H - MB) {
    doc.addPage();
    return MT;
  }
  return y;
};

/** Renders a bold section heading with a hairline rule underneath. Returns new y. */
const sectionHeading = (doc, text, y) => {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 40, 40);
  doc.text(text, ML, y);
  y += 2;
  doc.setDrawColor(180, 180, 180);
  doc.line(ML, y, PAGE_W - MR, y);
  return y + 6;
};

/**
 * Stamps footer on every page of an already-merged pdf-lib PDFDocument.
 * Runs after the merge so the true total page count is known across all sections.
 *   left   – "Powered by Sensing Clues"
 *   centre – "Printed by {username}"  (omitted when username is empty)
 *   right  – "Page x of n"
 */
const stampMergedFooters = async (mergedDoc, currentUser) => {
  const font = await mergedDoc.embedFont(StandardFonts.Helvetica);
  const grey = rgb(0.55, 0.55, 0.55);
  const fontSize = 8;
  const MM_TO_PT = 72 / 25.4;
  const footerY = 8 * MM_TO_PT; // 8 mm from bottom
  const marginL = ML * MM_TO_PT;
  const marginR = MR * MM_TO_PT;
  const total = mergedDoc.getPageCount();

  for (let i = 0; i < total; i++) {
    const page = mergedDoc.getPage(i);
    const { width } = page.getSize();

    // Left: powered by
    page.drawText('Powered by Sensing Clues', {
      x: marginL,
      y: footerY,
      size: fontSize,
      font,
      color: grey
    });

    // Centre: printed by (optional)
    if (currentUser) {
      const label = `Printed by ${currentUser}`;
      const labelW = font.widthOfTextAtSize(label, fontSize);
      page.drawText(label, {
        x: width / 2 - labelW / 2,
        y: footerY,
        size: fontSize,
        font,
        color: grey
      });
    }

    // Right: page x of n
    const pageLabel = `Page ${i + 1} of ${total}`;
    const pageLabelW = font.widthOfTextAtSize(pageLabel, fontSize);
    page.drawText(pageLabel, {
      x: width - marginR - pageLabelW,
      y: footerY,
      size: fontSize,
      font,
      color: grey
    });
  }
};

/** Grey placeholder box for images that could not be loaded. */
const drawPlaceholder = (doc, x, y, w, h) => {
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(240, 240, 240);
  doc.rect(x, y, w, h, 'FD');
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text('Image\nunavailable', x + w / 2, y + h / 2 - 1, {
    align: 'center'
  });
  doc.setTextColor(30, 30, 30);
};

/**
 * Build the main jsPDF document: header, details, photos, attachments list.
 * Returns the jsPDF instance (footers not yet stamped).
 */
const buildMainDoc = (
  data,
  specificDetailsList,
  imageMedia,
  imageDataURLs,
  pdfMedia,
  files,
  mapDataURLs
) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const obsClass = capitalize(
    data.observationClass || data.observationType || ''
  );
  const groupName = (data.group && data.group.groupName) || '';
  const agentName = capitalize(getPayloadAgentName(data));
  const when = data.when ? moment(data.when) : null;
  const dateStr = when ? when.format('DD MMM YYYY') : '';
  const timeStr = when ? when.format('HH:mm') : '';
  let locationStr = '';
  if (
    data.where &&
    Array.isArray(data.where.coordinates) &&
    data.where.coordinates.length >= 2
  ) {
    const lng = data.where.coordinates[0];
    const lat = data.where.coordinates[1];
    locationStr = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }

  // ────────────────────────────────────────────────────────
  // HEADER  – title left, info block right
  // ────────────────────────────────────────────────────────
  let y = MT;

  const titleMaxW = PAGE_W * 0.52 - ML;
  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  const titleLines = doc.splitTextToSize(obsClass, titleMaxW);
  doc.text(titleLines, ML, y + 7);
  const titleEndY = y + 7 + (titleLines.length - 1) * 8;

  const infoItems = [];
  if (groupName) infoItems.push(['Group', groupName]);
  if (agentName) infoItems.push(['Agent', agentName]);
  if (dateStr) infoItems.push(['Date', dateStr]);
  if (timeStr) infoItems.push(['Time', timeStr]);
  if (locationStr) infoItems.push(['Location', locationStr]);

  const rightX = PAGE_W - MR;
  let infoY = y + 5;
  doc.setFontSize(9);
  infoItems.forEach(([label, value]) => {
    const valW = doc.getTextWidth(value);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(90, 90, 90);
    doc.text(label + ': ', rightX - valW, infoY, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    doc.text(value, rightX, infoY, { align: 'right' });
    infoY += 6;
  });

  y = Math.max(titleEndY, infoY) + 4;

  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.4);
  doc.line(ML, y, PAGE_W - MR, y);
  doc.setLineWidth(0.2);
  y += 8;

  // ────────────────────────────────────────────────────────
  // SPECIFIC DETAILS
  // ────────────────────────────────────────────────────────
  if (specificDetailsList.length > 0) {
    y = checkPage(doc, y, 22);
    y = sectionHeading(doc, 'Details', y);

    const LABEL_COL_W = 58;
    const valueX = ML + LABEL_COL_W;
    const valueMaxW = CONTENT_W - LABEL_COL_W;

    doc.setFontSize(9);
    specificDetailsList.forEach(({ label, value }) => {
      y = checkPage(doc, y, 7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(label + ':', ML, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      const lines = doc.splitTextToSize(String(value || '-'), valueMaxW);
      doc.text(lines, valueX, y);
      y += Math.max(lines.length, 1) * 5 + 1;
    });
    y += 6;
  }

  // ────────────────────────────────────────────────────────
  // PHOTOS  – map thumbnails row, then observation photos (max 4/row)
  // ────────────────────────────────────────────────────────
  const hasMapThumbnails =
    mapDataURLs && (mapDataURLs.outdoors || mapDataURLs.satellite);
  if (imageMedia.length > 0 || hasMapThumbnails) {
    y = checkPage(doc, y, 22);
    y = sectionHeading(doc, 'Photos', y);

    // ── Map thumbnails (satellite + outdoors), side by side ──
    if (hasMapThumbnails) {
      const MAP_GAP = 5;
      const MAP_W = (CONTENT_W - MAP_GAP) / 2;
      const MAP_H = MAP_W * (375 / 600); // preserve API aspect ratio

      y = checkPage(doc, y, MAP_H + 10);

      const maps = ['satellite', 'outdoors'];
      maps.forEach((key, idx) => {
        const x = ML + idx * (MAP_W + MAP_GAP);
        const dataURL = mapDataURLs[key];
        if (dataURL) {
          try {
            doc.addImage(
              dataURL,
              'JPEG',
              x,
              y,
              MAP_W,
              MAP_H,
              undefined,
              'MEDIUM'
            );
          } catch {
            drawPlaceholder(doc, x, y, MAP_W, MAP_H);
          }
        } else {
          drawPlaceholder(doc, x, y, MAP_W, MAP_H);
        }
      });

      y += MAP_H + 6;
    }

    const PER_ROW = 4;
    const GAP = 3;
    const PHOTO_W = (CONTENT_W - (PER_ROW - 1) * GAP) / PER_ROW;
    const PHOTO_H = PHOTO_W;

    for (let i = 0; i < imageMedia.length; i += PER_ROW) {
      const rowItems = imageMedia.slice(i, i + PER_ROW);
      y = checkPage(doc, y, PHOTO_H + GAP + 2);

      rowItems.forEach((item, j) => {
        const x = ML + j * (PHOTO_W + GAP);
        const dataURL = imageDataURLs[i + j];
        if (dataURL) {
          try {
            doc.addImage(
              dataURL,
              'JPEG',
              x,
              y,
              PHOTO_W,
              PHOTO_H,
              undefined,
              'MEDIUM'
            );
          } catch {
            drawPlaceholder(doc, x, y, PHOTO_W, PHOTO_H);
          }
        } else {
          drawPlaceholder(doc, x, y, PHOTO_W, PHOTO_H);
        }
      });
      y += PHOTO_H + GAP;
    }
    y += 6;
  }

  // ────────────────────────────────────────────────────────
  // ATTACHMENTS LIST  – names only
  // ────────────────────────────────────────────────────────
  if (pdfMedia.length > 0 || files.length > 0) {
    y = checkPage(doc, y, 22);
    y = sectionHeading(doc, 'Attachments', y);

    doc.setFontSize(9);

    if (pdfMedia.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Documents:', ML, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      pdfMedia.forEach(item => {
        y = checkPage(doc, y, 6);
        doc.text(`\u2022  ${item.Media.name || 'Document'}`, ML + 4, y);
        y += 5;
      });
      y += 3;
    }

    if (files.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Files:', ML, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      files.forEach(item => {
        y = checkPage(doc, y, 6);
        doc.text(`\u2022  ${item.name || 'File'}`, ML + 4, y);
        y += 5;
      });
    }
  }

  return doc;
};

/**
 * Build the history + print-info jsPDF document (always created, starts fresh).
 * Shows history entries (if any) followed by a closing metadata block.
 */
const buildHistoryDoc = (
  editHistoryEntries,
  originalEntryMeta,
  data,
  currentUser
) => {
  const groupId = (data.group && data.group.groupRef) || data.groupRef || '';
  const observationId = data.observationId || '';

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  let y = MT;

  // ── History entries (only when present) ──────────────────
  if (editHistoryEntries && editHistoryEntries.length > 0) {
    y = sectionHeading(doc, 'History', y);

    doc.setFontSize(9);
    editHistoryEntries.forEach(entry => {
      y = checkPage(doc, y, 14);

      const editedOn =
        entry.when && moment(entry.when).isValid()
          ? moment(entry.when).format('DD MMM YYYY - HH:mm')
          : String(entry.when || '-');

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 55, 55);
      doc.text(`Edited: ${editedOn}  —  ${entry.agent || '-'}`, ML, y);
      y += 5;

      if (Array.isArray(entry.changes) && entry.changes.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        entry.changes.forEach(change => {
          y = checkPage(doc, y, 5);
          const str = `  ${change.attribute}: "${change.oldValue ||
            ''}"  →  "${change.newValue || ''}"`;
          const lines = doc.splitTextToSize(str, CONTENT_W - 4);
          doc.text(lines, ML + 3, y);
          y += lines.length * 5 + 1;
        });
      }
      y += 4;
    });

    if (
      originalEntryMeta &&
      (originalEntryMeta.when || originalEntryMeta.agent)
    ) {
      y = checkPage(doc, y, 7);
      const origWhen =
        originalEntryMeta.when && moment(originalEntryMeta.when).isValid()
          ? moment(originalEntryMeta.when).format('DD MMM YYYY - HH:mm')
          : String(originalEntryMeta.when || '-');
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(110, 110, 110);
      doc.text(
        `Original entry: ${origWhen}${
          originalEntryMeta.agent ? ', ' + originalEntryMeta.agent : ''
        }`,
        ML,
        y
      );
      y += 10;
    } else {
      y += 6;
    }
  }

  // ── Print metadata block ──────────────────────────────────
  y = checkPage(doc, y, 40);
  y = sectionHeading(doc, 'Print information', y);

  const printedBy = currentUser || '-';
  const metaRows = [
    ['Date of print', moment().format('DD MMM YYYY - HH:mm')],
    ['Printed by', printedBy],
    ['Group ID', groupId || '-'],
    ['Observation ID', observationId || '-']
  ];

  const LABEL_COL_W = 58;
  const valueX = ML + LABEL_COL_W;
  const valueMaxW = CONTENT_W - LABEL_COL_W;

  doc.setFontSize(9);
  metaRows.forEach(([label, value]) => {
    y = checkPage(doc, y, 7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text(label + ':', ML, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    const lines = doc.splitTextToSize(String(value), valueMaxW);
    doc.text(lines, valueX, y);
    y += Math.max(lines.length, 1) * 5 + 1;
  });

  return doc;
};

/**
 * Merge one or more ArrayBuffer PDFs into a pdf-lib PDFDocument.
 * Pages that fail to load are silently skipped.
 */
const appendPDFBytes = async (target, bytesList) => {
  for (const bytes of bytesList) {
    if (!bytes) continue;
    try {
      const src = await PDFDocument.load(bytes, {
        ignoreEncryption: true
      });
      const indices = src.getPageIndices();
      const copied = await target.copyPages(src, indices);
      copied.forEach(p => target.addPage(p));
    } catch {
      // skip unreadable PDFs
    }
  }
};

/**
 * Generates an observation PDF and opens it in a new browser tab.
 *
 * Order: header + details + photos + attachments list
 *        → full-page copies of each attached PDF
 *        → history (separate page)
 *
 * @param {object} data                – raw observation data from props.data
 * @param {Array}  specificDetailsList – [{label, value}] already filtered & formatted
 * @param {Array}  editHistoryEntries  – from getEditHistoryEntries()
 * @param {object} originalEntryMeta  – from getOriginalEntryMeta()
 */
export const generateObservationPDF = async (
  data,
  specificDetailsList,
  editHistoryEntries,
  originalEntryMeta,
  currentUser
) => {
  const allMedia = Array.isArray(data.media) ? data.media : [];
  const imageMedia = allMedia.filter(isImageMedia);
  const pdfMedia = allMedia.filter(isPDFMedia);
  const files = Array.isArray(data.files) ? data.files : [];

  // Resolve coordinates for map thumbnails
  const hasCoords =
    data.where &&
    Array.isArray(data.where.coordinates) &&
    data.where.coordinates.length >= 2;
  const lng = hasCoords ? data.where.coordinates[0] : null;
  const lat = hasCoords ? data.where.coordinates[1] : null;

  // Pre-load images, attached PDFs and map thumbnails in parallel
  const [
    imageDataURLs,
    attachedPDFBytes,
    mapSatellite,
    mapOutdoors
  ] = await Promise.all([
    Promise.all(
      imageMedia.map(item =>
        item.url ? loadImageAsDataURL(item.url) : Promise.resolve(null)
      )
    ),
    Promise.all(
      pdfMedia.map(item => (item.url ? fetchPDFBytes(item.url) : null))
    ),
    hasCoords
      ? fetchMapThumbnail(MAPBOX_SATELLITE_STYLE, lng, lat)
      : Promise.resolve(null),
    hasCoords
      ? fetchMapThumbnail(MAPBOX_OUTDOORS_STYLE, lng, lat)
      : Promise.resolve(null)
  ]);

  const mapDataURLs = { satellite: mapSatellite, outdoors: mapOutdoors };

  // Build the two jsPDF documents
  const mainDoc = buildMainDoc(
    data,
    specificDetailsList,
    imageMedia,
    imageDataURLs,
    pdfMedia,
    files,
    mapDataURLs
  );
  const historyDoc = buildHistoryDoc(
    editHistoryEntries,
    originalEntryMeta,
    data,
    currentUser
  );

  // ── Merge with pdf-lib ──────────────────────────────────
  const merged = await PDFDocument.create();

  // 1. Main section (header → attachments list)
  await appendPDFBytes(merged, [mainDoc.output('arraybuffer')]);

  // 2. Attached PDFs (full pages, in order)
  if (attachedPDFBytes.some(Boolean)) {
    await appendPDFBytes(merged, attachedPDFBytes);
  }

  // 3. History + print metadata section
  await appendPDFBytes(merged, [historyDoc.output('arraybuffer')]);

  // Stamp footers after merge – now the true total page count is known
  await stampMergedFooters(merged, currentUser);

  const mergedBytes = await merged.save();
  const blob = new Blob([mergedBytes], { type: 'application/pdf' });
  const blobURL = URL.createObjectURL(blob);
  window.open(blobURL, '_blank');
};
